<template>
  <v-form
    ref="form"
    class="setting-detail">
    <div class="toolbar-wrap">
      <div class="toolbar-hint">
        <v-icon
          small
          color="grey lighten-1"
          class="mr-1">
          mdi-drag
        </v-icon>
        <span class="caption grey--text">ลากเพื่อจัดลำดับ Priority</span>
      </div>
      <div class="d-flex gap-2">
        <v-btn
          v-if="!isProduction"
          color="error"
          depressed
          outlined
          class="mr-2"
          :disabled="loading"
          @click="resetAllToInactive()">
          <v-icon
            left
            small>
            mdi-restore
          </v-icon>
          Reset
        </v-btn>
        <v-btn
          color="primary"
          depressed
          outlined
          :loading="loading"
          :disabled="disableSaveWarehouse"
          @click="savePriorityWarehouse()">
          <v-icon
            left
            small>
            mdi-content-save
          </v-icon>
          Save
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="header"
      :items="warehouseData"
      :options.sync="options"
      :items-per-page="-1"
      :server-items-length="-1"
      :loading="loading"
      height="calc(100vh - 320px)"
      class="elevation-0 draggable-table"
      hide-default-footer>
      <template v-slot:body="{ headers }">
        <draggable
          :list="warehouseData"
          group="warehouses"
          handle=".drag-handle"
          tag="tbody">
          <tr
            v-for="(item, index) in warehouseData"
            :key="`warehouse-index-${index}`"
            class="table-row"
            :class="{ 'row-editing': editIndex === index }">
            <td
              class="drag-handle-cell"
              width="40">
              <v-icon
                class="drag-handle"
                small
                color="grey lighten-1">
                mdi-drag
              </v-icon>
            </td>
            <td
              class="text-start"
              :width="headers[1].width">
              <span class="caption grey--text">#{{ item.id }}</span>
            </td>
            <td
              class="text-start"
              :width="headers[3].width">
              <span class="font-weight-medium">{{ item.name }}</span>
            </td>
            <td
              v-for="ch in channels"
              :key="`ch-${ch.value}-${index}`"
              class="text-center channel-cell"
              :width="160">
              <div class="channel-cell-inner">
                <v-switch
                  v-model="warehouseData[index].channels[ch.value].status"
                  dense
                  hide-details
                  class="mt-0 pt-0 channel-switch"
                  true-value="active"
                  false-value="inactive" />
                <v-text-field
                  v-if="editIndex === index"
                  v-model="warehouseData[index].channels[ch.value].minStock"
                  :rules="numberFieldRules"
                  hide-details
                  :error="numberFieldRules.some((r) => r(warehouseData[index].channels[ch.value].minStock) !== true)"
                  class="text-center inline-edit-field"
                  outlined
                  dense
                  :readonly="loading"
                  placeholder="0" />
                <span
                  v-else
                  class="stock-value"
                  :class="{ 'stock-inactive': warehouseData[index].channels[ch.value].status !== 'active' }">
                  {{ warehouseData[index].channels[ch.value].minStock }}
                </span>
              </div>
            </td>
            <td
              class="text-center"
              :width="headers[2].width">
              <v-simple-checkbox
                v-model="warehouseData[index].priorityWarehouse"
                :ripple="false"
                color="primary" />
            </td>
            <td
              class="text-center"
              :width="80">
              <v-btn
                v-if="editIndex === index"
                icon
                x-small
                color="success"
                class="mr-1"
                :disabled="!isEditRowValid"
                @click="editIndex = null">
                <v-icon small>
                  mdi-check
                </v-icon>
              </v-btn>
              <v-btn
                v-if="editIndex === index"
                icon
                x-small
                color="grey"
                @click="cancelEdit(index)">
                <v-icon small>
                  mdi-close
                </v-icon>
              </v-btn>
              <v-btn
                v-else
                icon
                x-small
                class="edit-btn"
                @click="startEdit(index)">
                <v-icon small>
                  mdi-pencil
                </v-icon>
              </v-btn>
            </td>
          </tr>
        </draggable>
      </template>
    </v-data-table>
  </v-form>
</template>

<script>
import WarehouseProvider from '@/resources/WarehouseProvider'
import OrderfulfillmentSettingNewProvider from '@/resources/OrderfulfillmentSettingNewProvider'
import Draggable from 'vuedraggable'

const WarehouseService = new WarehouseProvider()
const SettingService = new OrderfulfillmentSettingNewProvider()

const CHANNELS = [
  { text: 'Web', value: 'web' },
  { text: 'Tiktok', value: 'tiktok' },
  { text: 'Lazada', value: 'lazada' }
]

const emptyChannelMap = () => CHANNELS.reduce((acc, ch) => {
  acc[ch.value] = { status: 'inactive', minStock: '0' }
  return acc
}, {})

export default {
  components: {
    Draggable
  },
  data () {
    return {
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 9999,
        sortBy: ['priority'],
        sortDesc: [false]
      },
      channels: CHANNELS,
      header: [
        { text: '', value: 'drag', width: 40, sortable: false },
        { text: 'ID', value: 'id', width: 60, sortable: false },
        { text: 'Warehouse Name', value: 'name', width: 200, sortable: false },
        ...CHANNELS.map((c) => ({
          text: c.text,
          value: c.value,
          align: 'center',
          width: 160,
          sortable: false
        })),
        { text: 'Priority WH', value: 'priorityWarehouse', width: 100, sortable: false, align: 'center' },
        { text: 'Actions', value: 'actions', sortable: false, width: 80, align: 'center' }
      ],
      editIndex: null,
      editBackup: null,
      warehouseData: [],
      memoWarehouses: [],
      numberFieldRules: [
        // eslint-disable-next-line no-mixed-operators
        (v) => v !== '' && v !== null && v !== undefined || 'Required.',
        (v) => /^[0-9]+$/.test(v) || 'Invalid.'
      ]
    }
  },
  computed: {
    isProduction () {
      return process.env.VUE_APP_MODE === 'PRODUCTION'
    },
    disableSaveWarehouse () {
      return JSON.stringify(this.memoWarehouses) === JSON.stringify(this.warehouseData) || this.loading
    },
    isEditRowValid () {
      if (this.editIndex === null) return true
      const row = this.warehouseData[this.editIndex]
      if (!row) return true
      return this.channels.every((ch) => {
        const val = row.channels[ch.value].minStock
        return this.numberFieldRules.every((rule) => rule(val) === true)
      })
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    resetData () {
      this.warehouseData = []
      this.memoWarehouses = []
      this.editIndex = null
      this.editBackup = null
    },
    resetAllToInactive () {
      this.editIndex = null
      this.editBackup = null
      this.warehouseData = this.warehouseData.map((w) => ({
        ...w,
        priorityWarehouse: false,
        channels: emptyChannelMap()
      }))
    },
    startEdit (index) {
      this.editBackup = JSON.parse(JSON.stringify(this.warehouseData[index].channels))
      this.editIndex = index
    },
    cancelEdit (index) {
      if (this.editBackup) {
        this.warehouseData[index].channels = this.editBackup
      }
      this.editIndex = null
      this.editBackup = null
    },
    /**
     * โหลด default rule จาก backend (4 channel docs) แล้ว merge เป็น 1 list
     */
    async fetchData () {
      this.resetData()
      try {
        this.loading = true
        const [{ data: warehouses }, { data: docs }] = await Promise.all([
          WarehouseService.getWarehouse(),
          SettingService.getMany({ is_default: 1 })
        ])
        const activeWarehouses = (warehouses ?? []).filter((w) => w.status === 'active')

        // index per-channel doc by channel name
        const docByChannel = {}
        docs.forEach((d) => { docByChannel[d.channel] = d })

        // build a unified list of warehouses with channels map
        // priority รวมเอาจาก channel ใดก็ได้ (sync ทุก channel เท่ากัน)
        const referenceDoc = docs[0]
        const warehouseOrder = referenceDoc
          ? referenceDoc.warehouses
              .slice()
              .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
              .map((w) => w.id)
          : activeWarehouses.map((w) => w.id)

        // build priorityWarehouse lookup from reference doc
        const priorityWarehouseMap = {}
        if (referenceDoc) {
          referenceDoc.warehouses.forEach((w) => {
            priorityWarehouseMap[w.id] = !!w.priorityWarehouse
          })
        }

        const warehouseMap = new Map()
        activeWarehouses.forEach((w) => {
          warehouseMap.set(w.id, {
            id: w.id,
            name: w.name,
            priority: warehouseOrder.indexOf(w.id),
            priorityWarehouse: priorityWarehouseMap[w.id] || false,
            channels: emptyChannelMap()
          })
        })

        CHANNELS.forEach((ch) => {
          const doc = docByChannel[ch.value]
          if (!doc) return
          doc.warehouses.forEach((wh) => {
            const target = warehouseMap.get(wh.id)
            if (!target) return
            target.channels[ch.value] = {
              status: wh.status || 'inactive',
              minStock: String(wh.minStock || 0)
            }
          })
        })

        this.warehouseData = [...warehouseMap.values()]
          .map((w, i) => ({ ...w, priority: w.priority === -1 ? activeWarehouses.length + i : w.priority }))
          .sort((a, b) => a.priority - b.priority)

        this.memoWarehouses = JSON.parse(JSON.stringify(this.warehouseData))
      } catch (error) {
        this.$store.dispatch('Components/setErrorPage', error.code)
      } finally {
        this.loading = false
      }
    },
    /**
     * ส่ง bulk update ไป backend (1 payload, backend วน 4 channels)
     * ส่งเป็น array of { channel, status, minStock } แทน map
     * เพื่อกัน CamelizeKeysPipe เข้าแปลงคีย์ของ map (เช่น online_international)
     */
    async savePriorityWarehouse () {
      if (!this.$refs.form.validate()) return
      try {
        this.loading = true
        await SettingService.saveDefaultGrouped({
          warehouses: this.warehouseData.map((w, i) => ({
            id: w.id,
            name: w.name,
            priority: i,
            priorityWarehouse: w.priorityWarehouse || false,
            channels: CHANNELS.map((ch) => ({
              channel: ch.value,
              status: w.channels[ch.value].status,
              minStock: Number(w.channels[ch.value].minStock || 0)
            }))
          }))
        })
        await this.fetchData()
      } catch (error) {
        this.$store.dispatch('Components/setErrorPage', error.code)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.setting-detail {
  background-color: #fff;
  border-radius: 4px;
}
.toolbar-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px;
}
.toolbar-hint {
  display: flex;
  align-items: center;
}
.table-row {
  transition: background-color 0.15s ease;
}
.table-row:hover {
  background-color: #f5f7fa !important;
}
.table-row.row-editing {
  background-color: #e8f5e9 !important;
}
.drag-handle {
  cursor: grab;
  opacity: 0.4;
  transition: opacity 0.15s;
}
.table-row:hover .drag-handle {
  opacity: 1;
}
.drag-handle-cell {
  padding: 0 4px !important;
}
.edit-btn {
  opacity: 0.3;
  transition: opacity 0.15s;
}
.table-row:hover .edit-btn {
  opacity: 1;
}
.channel-cell {
  vertical-align: middle;
}
.channel-cell-inner {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}
.channel-switch >>> .v-input--switch__track {
  margin: 0;
}
.stock-value {
  font-variant-numeric: tabular-nums;
  color: #555;
  min-width: 24px;
  text-align: center;
}
.stock-value.stock-inactive {
  opacity: 0.4;
}
.inline-edit-field {
  max-width: 70px;
  margin: 0 auto;
}
.text-center >>> input {
  text-align: center;
}
.theme--light.v-data-table.draggable-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row),
.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
  border-bottom: 1px solid #f0f0f0;
}
</style>
